// Layer 4 (UI) — Pending from Advisory, the right four columns of the
// Maintenance panel row (polish.P21, comp A `.ho`). The bearing action leads as
// the one decision object in the card (its draft pill, proposition and facts,
// and the button into its case page); the week-urgency actions follow as rows,
// each opening its case in the drawer. Presentational: every figure is a field
// of `pendingFromAdvisory()`.

import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { GlassCard, Pill } from '@/design-system';
import { cn } from '@/shared/cn';
import { ActorChip } from '../advisory/PipelineStrip';
import { gatePillClass } from '../advisory/gate-ui';
import type { PendingFromAdvisoryRead, PendingLead, PendingLeadFact } from './PendingFromAdvisory.transform';

const KICKER = 'text-xs font-semibold uppercase tracking-[0.07em] text-muted';

function Fact({ fact }: { fact: PendingLeadFact }) {
  return (
    <div className="grid grid-cols-[5.75rem_minmax(0,1fr)] items-baseline gap-x-2.5 py-1.5" data-pending-fact={fact.label}>
      <dt className={KICKER}>{fact.label}</dt>
      <dd className="min-w-0 text-[12.5px] leading-snug text-ink-primary">
        {fact.kind === 'text' ? (
          <>
            {fact.value}
            {fact.sub ? <span className="block text-xs text-muted">{fact.sub}</span> : null}
          </>
        ) : null}
        {fact.kind === 'handler' ? (
          <span className="inline-flex flex-wrap items-center gap-x-1.5">
            <ActorChip actor="Human">{fact.person}</ActorChip>
            <span className="text-xs text-muted">{fact.since}</span>
          </span>
        ) : null}
        {fact.kind === 'respond' ? (
          <>
            {fact.at}
            <span className="text-xs text-muted"> · {fact.tail}</span>
          </>
        ) : null}
        {fact.kind === 'stop' ? (
          <span className="flex min-w-0 flex-col items-start gap-1">
            <span className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
              <ActorChip actor="Human">{fact.decides}</ActorChip>
              <span>decides</span>
            </span>
            <span className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
              <ActorChip actor="Human">{fact.confirms}</ActorChip>
              <span>confirms</span>
            </span>
          </span>
        ) : null}
      </dd>
    </div>
  );
}

function Lead({
  lead,
  projectId,
  approvedPill,
}: {
  lead: PendingLead;
  projectId: string;
  approvedPill: string | null;
}) {
  return (
    <div className="flex flex-col gap-1.5 border-b border-[var(--color-divider)] pb-3" data-pending-lead={lead.caseId}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-mono text-xs tracking-wide text-muted">{lead.code}</span>
        <span className={KICKER}>{lead.stage}</span>
        <span className={gatePillClass(approvedPill ? 'approved' : 'draft')} data-pending-lead-pill>
          {approvedPill ?? 'Draft — not live until approved'}
        </span>
      </div>
      <p className="text-[14.5px] font-semibold leading-snug text-ink-primary" data-pending-lead-proposition>
        {lead.proposition}
      </p>
      <dl className="divide-y divide-[var(--color-divider)] tabular-nums">
        {lead.facts.map((fact) => (
          <Fact key={fact.label} fact={fact} />
        ))}
      </dl>
      <Link
        to={`/projects/${projectId}/advisory/case/${lead.caseId}`}
        className="mt-1 inline-flex items-center gap-1.5 self-start rounded-[9px] border border-[var(--hairline-strong)] bg-[var(--surface-2)] px-3 py-1.5 text-[12.5px] font-semibold text-ink-primary transition-colors hover:bg-[var(--surface-1)]"
        data-pending-case-link={lead.caseId}
      >
        {lead.caseTitle}
        <ArrowRight className="size-3" aria-hidden />
      </Link>
    </div>
  );
}

export function PendingFromAdvisory({
  read,
  projectId,
  approvedPill,
  onOpenCase,
}: {
  read: PendingFromAdvisoryRead;
  projectId: string;
  approvedPill: string | null;
  onOpenCase: (caseId: string, title: string) => void;
}) {
  if (!read.lead && read.rows.length === 0) return null;
  return (
    <GlassCard
      as="section"
      tone="matte"
      aria-label="Pending from Advisory"
      className="flex h-full min-w-0 flex-col px-4 py-3"
      data-pending-from-advisory
    >
      <header className="mb-2 flex flex-wrap items-center gap-2">
        <h3 className="text-sm font-semibold text-ink-primary">Pending from Advisory</h3>
        <span className="flex-1" />
        <span className="text-xs tabular-nums text-muted" data-pending-meta>{read.meta}</span>
      </header>
      {read.lead ? <Lead lead={read.lead} projectId={projectId} approvedPill={approvedPill} /> : null}
      <ul className="divide-y divide-[var(--color-divider)]">
        {read.rows.map((row) => (
          <li key={row.id}>
            <button
              type="button"
              onClick={() => onOpenCase(row.caseId, row.text)}
              className={cn(
                'grid w-full grid-cols-[minmax(0,1fr)_auto] gap-x-2.5 gap-y-0.5 py-2.5 text-left transition-colors duration-150 hover:bg-[var(--surface-1)]',
              )}
              data-pending-row={row.id}
            >
              <span className="text-[12.5px] font-semibold leading-snug text-ink-primary">{row.text}</span>
              <span className="self-start">
                <Pill tone="default">{row.urgencyLabel}</Pill>
              </span>
              <span className="text-xs text-muted">
                <span className="font-mono">{row.id}</span>
                {' · '}
                <span className="font-medium text-accent-700 dark:text-accent-400">{row.caseId}</span>
              </span>
            </button>
          </li>
        ))}
      </ul>
    </GlassCard>
  );
}
